// F-MM-N31 — staff meal allowances in Manage Staff. Pure helpers, tested in
// staffAllowances.test.ts.
import type { HouseAccountStatementEntry } from '@/types'

/** Why the current user can't change an allowance, or null when they can.
 *  Mirrors the server (owner decisions 2026-10-13); the server still checks. */
export function allowanceChangeBlock(
  callerRole: string,
  callerId: string,
  ownerUserId: string | null | undefined,
): string | null {
  if (callerRole !== 'admin') return 'Only an admin can give or change a meal allowance.'
  if (ownerUserId && callerId && ownerUserId === callerId) {
    return "You can't change your own meal allowance. Ask another admin."
  }
  return null
}

/** First and last day (YYYY-MM-DD) of a YYYY-MM business month. */
export function monthBounds(cycleMonth: string): { from: string; to: string } {
  const [y, m] = cycleMonth.split('-').map(Number)
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  const mm = String(m).padStart(2, '0')
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(last).padStart(2, '0')}` }
}

/** "October" for "2026-10". */
export function monthName(cycleMonth: string): string {
  const [y, m] = cycleMonth.split('-').map(Number)
  if (!y || !m) return 'this month'
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-GB', { month: 'long', timeZone: 'UTC' })
}

export interface AllowanceMeal {
  orderId: string
  orderNumber: string
  date: string
  bill: number
  over: number
  approvedBy?: string
  undone: boolean
}

/** A month's meals from a staff statement, newest first. An undone meal stays
 *  on the list, marked, so the count matches what happened at the counter. */
export function mealsFromStatement(entries: HouseAccountStatementEntry[]): AllowanceMeal[] {
  const reversed = new Set(entries.filter((e) => e.entry_type === 'charge_reversal' && e.order_id).map((e) => e.order_id))
  return entries
    .filter((e) => e.entry_type === 'charge' && e.order_id)
    .map((e) => ({
      orderId: e.order_id!,
      orderNumber: e.order_number ?? '',
      date: e.business_date ?? e.created_at.slice(0, 10),
      bill: e.amount,
      over: e.overage,
      approvedBy: e.approved_by,
      undone: reversed.has(e.order_id),
    }))
    .reverse()
}

/** Left this month, never below zero, and how far over. */
export function allowanceLeft(allowance: number, used: number, over: number): { left: number; over: number } {
  return { left: Math.max(0, Math.round((allowance - used) * 100) / 100), over: Math.max(0, over) }
}
